/**
 * Dropdown "Revisioni" — mostra tutta la catena di versioni di un preventivo
 * e permette di crearne una nuova (Crea revisione).
 *
 * "Crea revisione" duplica il preventivo + tutte le sue stratigrafie in un
 * nuovo preventivo `draft` con version+1 e parent_estimate_id puntato al sorgente.
 * Pratico per modificare i prezzi/composizione senza perdere lo storico
 * (es. cliente chiede sconto → nuova revisione invece di toccare l'offerta inviata).
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { GitBranch, Plus, Check } from 'lucide-react';
import { useEstimateRevisions, useCreateRevision } from '@/hooks/useEstimateRevisions';
import EstimateStatusBadge from './EstimateStatusBadge';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyObj = Record<string, any>;

interface EstimateRevisionsDropdownProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  estimate: AnyObj;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  stratigraphies: AnyObj[];
}

const EstimateRevisionsDropdown: React.FC<EstimateRevisionsDropdownProps> = ({ estimate, stratigraphies }) => {
  const navigate = useNavigate();
  const { data: revisions = [], isLoading } = useEstimateRevisions(estimate?.id);
  const { createRevision, isCreating } = useCreateRevision();

  const currentVersion = Number(estimate?.version ?? 1);
  const totalVersions = revisions.length || 1;

  const handleCreateRevision = () => {
    // Le stratigrafie passate dal parent sono in formato "frontend" (camelCase),
    // mentre il DB vuole snake_case. Adatto qui i campi essenziali.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const adapted = stratigraphies.map((s: any) => ({
      stratigraphy_id: s.stratigraphyId ?? s.stratigraphy_id ?? null,
      name: s.name,
      description: s.description,
      area: s.area,
      quantity: s.quantity ?? 1,
      unit_cost: s.unitCost ?? s.unit_cost,
      total_cost: s.totalCost ?? s.total_cost,
      stratigraphy_data: s.stratigraphyData ?? s.stratigraphy_data ?? null,
      layers_data: s.layersData ?? s.layers_data ?? null,
      prices_updated_at: s.pricesUpdatedAt ?? s.prices_updated_at ?? null,
      original_stratigraphy_id: s.originalStratigraphyId ?? s.original_stratigraphy_id ?? null,
      finish_level: s.finishLevel ?? s.finish_level ?? null,
      finish_cost_per_sqm: s.finishCostPerSqm ?? s.finish_cost_per_sqm ?? null,
      finish_components_data: s.finishComponentsData ?? s.finish_components_data ?? null,
      wall_height: s.wallHeight ?? s.wall_height ?? null,
      calculated_materials: s.calculatedMaterials ?? s.calculated_materials ?? null,
      stud_spacing_mm: s.studSpacingMm ?? s.stud_spacing_mm ?? null,
      frame_coupling: s.frameCoupling ?? s.frame_coupling ?? null,
    }));

    // Adatta anche l'estimate ai nomi snake_case attesi dall'hook
    const adaptedEstimate = {
      id: estimate.id,
      name: estimate.name,
      description: estimate.description,
      project_id: estimate.projectId ?? estimate.project_id,
      version: estimate.version ?? 1,
      total_amount: estimate.totalAmount ?? estimate.total_amount ?? 0,
      notes: estimate.notes,
    };

    createRevision({ sourceEstimate: adaptedEstimate, sourceStratigraphies: adapted });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1.5">
          <GitBranch className="h-4 w-4" />
          <span>Revisioni</span>
          <Badge variant="secondary" className="ml-1 px-1.5 py-0 h-5 text-[10px]">
            v{currentVersion}/{totalVersions}
          </Badge>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <DropdownMenuLabel>Storico revisioni</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {isLoading ? (
          <div className="px-2 py-3 text-xs text-muted-foreground">Caricamento…</div>
        ) : revisions.length === 0 ? (
          <div className="px-2 py-3 text-xs text-muted-foreground">
            Nessuna revisione. Sei sulla v1 (originale).
          </div>
        ) : (
          revisions.map(rev => {
            const isCurrent = rev.id === estimate.id;
            return (
              <DropdownMenuItem
                key={rev.id}
                disabled={isCurrent}
                onClick={() => !isCurrent && navigate(`/estimates/${rev.id}/manage`)}
                className="flex items-start gap-2"
              >
                <div className="flex items-center justify-center w-6 h-6 rounded-full bg-muted text-xs font-bold shrink-0 mt-0.5">
                  v{rev.version}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`text-sm truncate ${isCurrent ? 'font-semibold' : ''}`}>
                      {rev.name}
                    </span>
                    {isCurrent && <Check className="h-3.5 w-3.5 text-emerald-600 shrink-0" />}
                  </div>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <EstimateStatusBadge status={rev.status} size="sm" />
                    <span className="text-[10px] text-muted-foreground">
                      {new Date(rev.created_at).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </span>
                  </div>
                </div>
              </DropdownMenuItem>
            );
          })
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          disabled={isCreating}
          onClick={(e) => { e.preventDefault(); handleCreateRevision(); }}
          className="gap-2 font-medium text-primary focus:text-primary"
        >
          <Plus className="h-4 w-4" />
          {isCreating ? 'Creazione…' : 'Crea nuova revisione'}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default EstimateRevisionsDropdown;
